// =============================================================================
// hsCsv (5.19.0) — the browser-side stripping of a HealthSherpa export before upload. The raw file never
// leaves the PC: we parse it here (comma or tab; quotes, "" escapes and embedded newlines handled), keep
// ONLY the allowlisted columns (the same list as the server's policy.EXPORT_COLUMNS), and refuse if any kept
// value looks like an SSN (ddd-dd-dddd). The server checks everything again.
// 5.20.0: the 53 columns of Henry's hs-export-clean.ps1 (BASE_COLUMNS) + the household members' NAME columns
// (MEMBER_COLUMNS: spouse_* and other_1..10_* first name, last name, applying). Keep this list identical to
// backend/services/healthsherpa/policy.py.
// 5.46.0 — Henry's decision 2026-11-02 (replaces the old "no date of birth" rule): personal info for quoting IS kept —
// per member date of birth, tobacco use, gender, plus household income / size — but only the columns an admin has
// CONFIRMED (the upload card's confirm step: column NAMES + what each maps to, never values). detectPersonalColumn()
// classifies a column by name with the same rules as the server (personal.detect_target). SSN stays NEVER kept:
// SSN columns are always stripped here, and any SSN-shaped value in a kept column refuses the whole upload.
// =============================================================================

export const BASE_COLUMNS = [
  'transaction_id', 'first_name', 'last_name', 'phone', 'email', 'address', 'city', 'zip_code',
  'effective_date', 'expiration_date', 'issuer', 'plan_name', 'metal_level', 'premium', 'subsidy', 'net_premium',
  'policy_status', 'effectuated', 'date_effectuated', 'date_cancelled', 'premium_paid', 'paid_through_date',
  'grace_period_start_date', 'past_due_member_responsibility_balance_due',
  'current_member_responsibility_balance_due', 'policy_status_notes', 'dmi_outstanding_count',
  'svi_outstanding_count', 'ffm_app_id', 'ffm_subscriber_id', 'healthsherpa_id', 'healthsherpa_policy_ids',
  'issuer_assigned_policy_id', 'external_id', 'client_details_link', 'submitted', 'submission_date',
  'submission_type', 'last_submission_date', 'renewal', 'archived', 'review_status', 'consent_status',
  'application_creation_date', 'application_claimed_date', 'last_ede_sync', 'agent', 'npn_used', 'npn_reason',
  'policy_aor', 'agent_agency_name', 'submitting_agent_name', 'submitting_agent_npn',
];
export const OTHER_MEMBER_SLOTS = 10;
const slots = Array.from({ length: OTHER_MEMBER_SLOTS }, (_x, i) => i + 1);
export const MEMBER_COLUMNS = [
  'spouse_first_name', 'spouse_last_name', 'spouse_applying',
  ...slots.map((n) => `other_${n}_first_name`),
  ...slots.map((n) => `other_${n}_last_name`),
  ...slots.map((n) => `other_${n}_applying`),
];
export const EXPORT_COLUMNS = [...BASE_COLUMNS, ...MEMBER_COLUMNS];
export const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/;

// --- 5.46.0 personal-info columns (same rules as backend/services/healthsherpa/personal.py) ----------------------------
export const MEMBER_SLOTS = ['primary', 'spouse', ...slots.map((n) => `other_${n}`)];
export const PI_FIELDS = ['dob', 'tobacco', 'gender'];
export const PI_TARGETS = [...MEMBER_SLOTS.flatMap((m) => PI_FIELDS.map((f) => `${m}.${f}`)), 'household.income', 'household.size', 'ignore'];
export const SSN_KEY_RE = /ssn|social.?sec/i;
const colKey = (n) => String(n || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const DOB_RE = /(date_?of_?birth|birth_?date|birthdate|birthday|(^|_)dob($|_)|(^|_)birth($|_))/;
const TOBACCO_RE = /(tobacco|smoker|smoking|nicotine)/;
const GENDER_RE = /((^|_)gender($|_)|(^|_)sex($|_))/;
const INCOME_RE = /income/;
const SIZE_RE = /(household_?size|family_?size|tax_?household_?size|household_?members?_?count|number_?of_?(people|members))/;
const OTHER_RE = /(?:^|_)(?:other|dependent|dependant|member|child)_?(\d{1,2})(?:_|$)/;

export function detectPersonalColumn(name) {
  if (EXPORT_COLUMNS.includes(name)) return null;
  const k = colKey(name);
  if (!k || SSN_KEY_RE.test(k)) return null;
  if (SIZE_RE.test(k)) return 'household.size';
  if (INCOME_RE.test(k)) return 'household.income';
  const field = DOB_RE.test(k) ? 'dob' : TOBACCO_RE.test(k) ? 'tobacco' : GENDER_RE.test(k) ? 'gender' : null;
  if (!field) return null;
  const m = k.match(OTHER_RE);
  let slot = 'primary';
  if (k.startsWith('spouse')) slot = 'spouse';
  else if (m) {
    const n = Number(m[1]);
    if (n < 1 || n > OTHER_MEMBER_SLOTS) return null;
    slot = `other_${n}`;
  }
  return `${slot}.${field}`;
}

const FIELD_LABEL = { dob: 'date of birth', tobacco: 'tobacco use', gender: 'gender' };
export function targetLabel(t) {
  if (t === 'ignore') return "Don't keep";
  if (t === 'household.income') return 'Household income';
  if (t === 'household.size') return 'Household size';
  const [slot, field] = String(t || '').split('.');
  const who = slot === 'primary' ? 'Primary' : slot === 'spouse' ? 'Spouse' : `Other member ${slot.replace('other_', '')}`;
  return `${who} — ${FIELD_LABEL[field] || field}`;
}
export const MAX_ROWS = 10000;

// RFC 4180-style parser. Returns an array of records (arrays of strings).
export function parseCsv(text) {
  let s = String(text || '');
  if (s.charCodeAt(0) === 0xfeff) s = s.slice(1);
  const firstLineEnd = s.search(/\r?\n/);
  const firstLine = firstLineEnd === -1 ? s : s.slice(0, firstLineEnd);
  const delim = firstLine.includes('\t') ? '\t' : ',';
  const records = [];
  let rec = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const n = s.length;
  while (i < n) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      field += ch; i += 1; continue;
    }
    if (ch === '"' && field === '') { inQuotes = true; i += 1; continue; }
    if (ch === delim) { rec.push(field); field = ''; i += 1; continue; }
    if (ch === '\r' && s[i + 1] === '\n') { rec.push(field); records.push(rec); rec = []; field = ''; i += 2; continue; }
    if (ch === '\n' || ch === '\r') { rec.push(field); records.push(rec); rec = []; field = ''; i += 1; continue; }
    field += ch; i += 1;
  }
  if (inQuotes) throw new Error('The file has an unclosed quote — is it a complete HealthSherpa export?');
  if (field !== '' || rec.length) { rec.push(field); records.push(rec); }
  return { delimiter: delim, records: records.filter((r) => !(r.length === 1 && r[0].trim() === '')) };
}

// -> { rows: [{column: value}], columns: [kept], dropped: [names], delimiter, personal, ssnColumns }. Throws (never
// shows a value) when a kept value looks like an SSN, or the file isn't a HealthSherpa export.
// 5.46.0: `confirmed` = the saved mapping {column name: target}. Confirmed personal columns (target not 'ignore') are
// kept under their own names (the server applies the mapping again); personal = [{name, detected, confirmed}] for every
// column that is or looks personal; ssnColumns = SSN-looking column names (always stripped).
export function stripExport(text, confirmed = {}) {
  const { delimiter, records } = parseCsv(text);
  if (records.length < 1) throw new Error('The file is empty');
  const header = records[0].map((h) => h.trim());
  const keep = [];
  const personal = [];
  const ssnColumns = header.filter((h) => h && !EXPORT_COLUMNS.includes(h) && SSN_KEY_RE.test(colKey(h)));
  header.forEach((h, idx) => {
    if (!h || keep.some((k) => k.name === h)) return;
    if (EXPORT_COLUMNS.includes(h)) { keep.push({ name: h, idx }); return; }
    if (SSN_KEY_RE.test(colKey(h))) return;
    const detected = detectPersonalColumn(h);
    const conf = Object.prototype.hasOwnProperty.call(confirmed || {}, h) ? confirmed[h] : null;
    if (detected || conf) personal.push({ name: h, detected, confirmed: conf });
    if (conf && conf !== 'ignore') keep.push({ name: h, idx });
  });
  if (!keep.some((k) => k.name === 'transaction_id')) throw new Error('This is not a HealthSherpa applications export (no transaction_id column)');
  const keptNames = keep.map((k) => k.name);
  const dropped = header.filter((h) => h && !keptNames.includes(h));
  const body = records.slice(1);
  if (body.length > MAX_ROWS) throw new Error(`Too many rows (at most ${MAX_ROWS.toLocaleString()})`);
  const rows = body.map((r, line) => {
    const o = {};
    keep.forEach(({ name, idx }) => {
      const v = (r[idx] ?? '').trim();
      if (SSN_RE.test(v)) throw new Error(`Row ${line + 2}, column ${name}: a value shaped like an SSN — nothing was uploaded`);
      o[name] = v;
    });
    return o;
  });
  return { rows, columns: keep.filter((k) => EXPORT_COLUMNS.includes(k.name)).map((k) => k.name), dropped, delimiter,
    personal, ssnColumns, personalKept: keep.filter((k) => !EXPORT_COLUMNS.includes(k.name)).map((k) => k.name) };
}
